import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApi } from '@/hooks/useApi';
import UploadDocumentModal from './components/UploadDocumentModal';
import ReviewDocumentModal from './components/ReviewDocumentModal';
import DocumentDetailModal from './components/DocumentDetailModal';
import DeleteConfirmModal from '@/components/base/DeleteConfirmModal';
import DocumentThumbnail from '@/components/feature/DocumentThumbnail';

interface Document {
  id: number;
  docType: string;
  docCategory: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  verificationStatus: string;
  customerName: string;
  uploadedBy: string;
  uploadedByName: string;
  reviewedByName: string | null;
  reviewReason: string | null;
  reviewedAt: string | null;
  createdAt: string;
  companyId: number;
  ocr_data?: {
    rawText: string;
    fields: Record<string, string | null>;
    confidence: number;
    processedAt: string;
    engine: string;
  } | null;
  verification_result?: {
    compositeScore: number;
    riskLevel: string;
    flags: { type: string; label: string; severity: string }[];
    processedAt: string;
  } | null;
}

export default function Documents() {
  const { user } = useAuth();
  const { data: documents, loading, refetch } = useApiQuery<Document[]>({ endpoint: '/documents' });
  const { del } = useApi();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [reviewDoc, setReviewDoc] = useState<Document | null>(null);
  const [detailDoc, setDetailDoc] = useState<Document | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<Document | null>(null);

  const canReview = user?.role === 'superadmin' || user?.role === 'company_admin' || user?.role === 'kyc_officer';
  const canDelete = canReview;

  const handleDelete = useCallback(async () => {
    if (!deleteDoc) return;
    await del(`/documents/${deleteDoc.id}`);
    setDeleteDoc(null);
    refetch();
  }, [deleteDoc, del, refetch]);

  const filtered = documents?.filter((d) => {
    const matchesSearch = (d.fileName || '').toLowerCase().includes(search.toLowerCase()) ||
      (d.customerName || '').toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || d.docCategory === category;
    return matchesSearch && matchesCategory;
  });

  const statusColors: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700',
    verified: 'bg-emerald-50 text-emerald-700',
    failed: 'bg-red-50 text-red-700',
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Documents</h1>
          <p className="text-stone-500 text-sm mt-1">Manage uploaded verification documents securely.</p>
        </div>
        <button
          onClick={() => setIsUploadOpen(true)}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-upload-cloud-line" />
          Upload Document
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'identity', 'address', 'photo', 'signature'].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize whitespace-nowrap ${
                  category === c ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {c === 'all' ? 'All' : c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-100 p-5 h-48 animate-pulse" />
          ))
        ) : (
          filtered?.map((doc) => (
            <div key={doc.id} className="bg-white rounded-xl border border-stone-100 p-5 hover:shadow-md transition-shadow group">
              <div className="flex items-start justify-between mb-4">
                <DocumentThumbnail
                  documentId={doc.id}
                  mimeType={doc.mimeType}
                  fileName={doc.fileName}
                  size="md"
                  rounded="lg"
                />
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[doc.verificationStatus] || 'bg-stone-100 text-stone-700'}`}>
                    {doc.verificationStatus}
                  </span>
                  {doc.ocr_data && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700 flex items-center gap-0.5">
                      <i className="ri-scan-line" />
                      OCR
                    </span>
                  )}
                  {doc.verification_result && doc.verification_result.riskLevel !== 'low' && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-0.5 ${
                      doc.verification_result.riskLevel === 'high'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      <i className="ri-alarm-warning-line" />
                      {doc.verification_result.riskLevel === 'high' ? 'Suspicious' : 'Caution'}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setDetailDoc(doc)}
                className="text-stone-900 text-sm font-medium truncate mb-1 text-left hover:text-brand-600 transition-colors cursor-pointer w-full"
              >
                {doc.fileName}
              </button>
              <p className="text-stone-400 text-xs mb-3">{doc.docType} · {formatSize(doc.fileSize)}</p>
              <div className="flex items-center justify-between text-xs text-stone-500">
                <span>{doc.customerName}</span>
                <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
              </div>

              {doc.verificationStatus === 'failed' && doc.reviewReason && (
                <div className="mt-3 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                  <p className="text-red-600 text-[10px] leading-tight">
                    <span className="font-semibold">Disapproved:</span> {doc.reviewReason}
                  </p>
                </div>
              )}

              {doc.verificationStatus === 'verified' && doc.reviewedByName && (
                <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-1.5">
                  <p className="text-emerald-600 text-[10px] leading-tight">
                    <span className="font-semibold">Approved by:</span> {doc.reviewedByName}
                    {doc.reviewedAt && ` · ${new Date(doc.reviewedAt).toLocaleDateString()}`}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-stone-50 flex-wrap">
                <a
                  href={`${API_BASE}/documents/${doc.id}/download?inline=true`}
                  className="flex-1 py-2 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-600 text-xs font-medium transition-colors text-center"
                >
                  View
                </a>
                <a
                  href={`${API_BASE}/documents/${doc.id}/download`}
                  className="flex-1 py-2 rounded-lg bg-stone-50 hover:bg-stone-100 text-stone-600 text-xs font-medium transition-colors text-center"
                >
                  Download
                </a>
                {canReview && doc.verificationStatus === 'pending' && (
                  <button
                    onClick={() => setReviewDoc(doc)}
                    className="flex-1 py-2 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 text-xs font-medium transition-colors text-center"
                  >
                    Review
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => setDeleteDoc(doc)}
                    className="flex-1 py-2 rounded-lg bg-stone-50 hover:bg-red-50 text-stone-400 hover:text-red-600 text-xs font-medium transition-colors text-center"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <UploadDocumentModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onSuccess={refetch}
      />

      <ReviewDocumentModal
        isOpen={!!reviewDoc}
        onClose={() => setReviewDoc(null)}
        onSuccess={refetch}
        documentId={reviewDoc?.id || null}
        documentName={reviewDoc?.fileName || ''}
        mimeType={reviewDoc?.mimeType || ''}
      />

      <DocumentDetailModal
        isOpen={!!detailDoc}
        onClose={() => setDetailDoc(null)}
        documentId={detailDoc?.id || null}
        onReviewSuccess={refetch}
      />

      <DeleteConfirmModal
        isOpen={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        onConfirm={handleDelete}
        entityName={deleteDoc?.fileName}
        description="The document record and physical file will be permanently removed."
      />
    </div>
  );
}
